import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Table, DatePicker, Spin, message, Statistic, Select, Button, Typography, Space as AntSpace } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  UserOutlined,
  RiseOutlined,
  FallOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { Line, Pie, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import Header from "../../components/Header";
import Layout from '../../layout/Layout';
import './dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { Option } = Select;
const { Title: TypographyTitle } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [setupDate, setSetupDate] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    summary: {},
    sales: [],
    purchases: [],
    expenses: [],
    customers: [],
    salesChart: [],
    paymentMethods: {},
    categoryBreakdown: {
      bar_amount: 0,
      food_amount: 0,
      shisha_amount: 0
    },
    billSummary: {
      pending_bills: 0,
      pending_amount: 0,
      paid_bills: 0,
      paid_amount: 0
    },
    entertainment: {
      total_entertainment: 0,
      entertainment_bills_count: 0
    },
    weeklySales: [],
    bestSellingItems: {
      bar: { summary: {}, items: [] },
      food: { summary: {}, items: [] }
    }
  });
  const [viewMode, setViewMode] = useState('daily'); // daily, weekly, monthly
  const [bestSellingSort, setBestSellingSort] = useState('value'); // 'value' or 'quantity'

  // Fetch setup date from API
  const fetchSetupDate = async () => {
    try {
      const headers = getHeaders();
      const response = await axios.get('/admin/dashboard/setup-date', headers);
      if (response.data.success && response.data.data) {
        setSetupDate(response.data.data.setup_date);
        return response.data.data.setup_date;
      }
    } catch (error) {
      console.error('Error fetching setup date:', error);
      message.error('Failed to load setup date');
      // Fallback to today's date if API fails
      const today = new Date().toISOString().split('T')[0];
      setSetupDate(today);
      return today;
    }
  };

  // Fetch setup date on component mount
  useEffect(() => {
    fetchSetupDate();
  }, []);

  useEffect(() => {
    if (setupDate) {
      fetchDashboardData();
    }
  }, [viewMode, setupDate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const lastMonth = new Date(new Date(setupDate).getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const headers = getHeaders();

      // Fetch all data in parallel
      const [
        summaryRes,
        salesChartRes,
        weeklySalesRes,
        purchasesRes,
        expensesRes,
        customersRes,
        accountSummaryRes,
        bestSellingRes,
        entertainmentRes,
        paidSummaryRes,
        pendingBillsRes
      ] = await Promise.all([
        axios.get(`/admin/dashboard/summary?setup_dte=${setupDate}`, headers),
        viewMode === 'daily' 
          ? axios.get(`/admin/dashboard/daily-sales?start_date=${lastMonth}&end_date=${setupDate}`, headers)
          : viewMode === 'weekly'
          ? axios.get(`/admin/dashboard/weekly-sales`, headers)
          : axios.get(`/admin/dashboard/monthly-sales`, headers),
        axios.get(`/admin/dashboard/weekly-sales`, headers),
        axios.get(`/admin/dashboard/purchase-summary?start_date=${lastMonth}&end_date=${setupDate}`, headers),
        axios.get(`/admin/dashboard/expenses-summary?start_date=${lastMonth}&end_date=${setupDate}`, headers),
        axios.get(`/admin/dashboard/customer-outstanding`, headers),
        axios.get('/account/dashboard-summary', headers),
        axios.get(`/admin/dashboard/best-selling-items?setup_dte=${setupDate}&limit=5`, headers),
        axios.get(`/admin/dashboard/entertainment?setup_dte=${setupDate}`, headers),
        axios.get(`/admin/dashboard/paid-summary?setup_dte=${setupDate}`, headers),
        axios.get(`/admin/dashboard/pending-bills-summary?setup_dte=${setupDate}`, headers)
      ]);

      console.log('=== VIEW MODE CHANGE DEBUG ===');
      console.log('Current View Mode:', viewMode);
      console.log('Sales Chart Response:', salesChartRes.data);
      console.log('Sales Chart Data Structure:', salesChartRes.data?.data);
      
      // More flexible data extraction
      let weeklyData = [];
      if (weeklySalesRes.data) {
        // Try different possible response structures
        if (weeklySalesRes.data.data?.weekly_breakdown) {
          weeklyData = weeklySalesRes.data.data.weekly_breakdown;
        } else if (weeklySalesRes.data.weekly_breakdown) {
          weeklyData = weeklySalesRes.data.weekly_breakdown;
        } else if (Array.isArray(weeklySalesRes.data.data)) {
          weeklyData = weeklySalesRes.data.data;
        } else if (Array.isArray(weeklySalesRes.data)) {
          weeklyData = weeklySalesRes.data;
        }
      }

      // Extract sales chart data based on view mode
      let salesChartData = [];
      let paymentMethodsData = {};
      
      try {
        if (viewMode === 'daily') {
          salesChartData = salesChartRes.data?.data?.daily_records || [];
          paymentMethodsData = salesChartRes.data?.data?.summary?.payment_methods || {};
        } else if (viewMode === 'weekly') {
          salesChartData = salesChartRes.data?.data?.weekly_breakdown || [];
          paymentMethodsData = salesChartRes.data?.data?.overall_summary?.payment_methods || {};
        } else {
          salesChartData = salesChartRes.data?.data?.monthly_breakdown || [];
          paymentMethodsData = salesChartRes.data?.data?.overall_summary?.payment_methods || {};
        }
        
        console.log('Extracted Sales Chart Data:', salesChartData);
        console.log('Extracted Payment Methods:', paymentMethodsData);
      } catch (extractError) {
        console.error('Error extracting sales chart data:', extractError);
        message.error(`Failed to process ${viewMode} data`);
      }

      console.log('=== END VIEW MODE DEBUG ===');

      setDashboardData({
        summary: summaryRes.data.data,
        salesChart: salesChartData,
        weeklySales: weeklyData,
        purchases: purchasesRes.data.data.by_category || [],
        expenses: expensesRes.data.data.detailed_breakdown || [],
        customers: customersRes.data.data.all_customers || [],
        paymentMethods: paymentMethodsData,
        categoryBreakdown: accountSummaryRes.data.data?.final_bill_summary?.category_breakdown || {
          bar_amount: 0,
          food_amount: 0,
          shisha_amount: 0
        },
        billSummary: {
          pending_bills: pendingBillsRes.data.success ? pendingBillsRes.data.summary?.total_bills || 0 : 0,
          pending_amount: pendingBillsRes.data.success ? pendingBillsRes.data.summary?.total_net_amount || 0 : 0,
          paid_bills: paidSummaryRes.data.success ? paidSummaryRes.data.summary?.total_bills || 0 : 0,
          paid_amount: paidSummaryRes.data.success ? 
            ((paidSummaryRes.data.summary?.total_net_amount || 0) - (paidSummaryRes.data.summary?.total_discount || 0)) : 0
        },
        entertainment: {
          total_entertainment: entertainmentRes.data.success ? entertainmentRes.data.data?.total_entertainment || 0 : 0,
          entertainment_bills_count: entertainmentRes.data.success ? entertainmentRes.data.data?.entertainment_bills?.length || 0 : 0,
          entertainment_bills: entertainmentRes.data.success ? entertainmentRes.data.data?.entertainment_bills || [] : []
        },
        bestSellingItems: bestSellingRes.data.success ? bestSellingRes.data.data : {
          bar: { summary: {}, items: [] },
          food: { summary: {}, items: [] }
        }
      });
      
      message.success(`Loaded ${viewMode} view data successfully`);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response?.data);
      message.error(`Failed to load ${viewMode} dashboard data: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Summary Cards Component
  const SummaryCards = () => {
    const cards = [
      {
        title: 'Total Sales',
        value: dashboardData.summary.sales?.total_sale || 0,
        icon: <DollarOutlined />,
        color: '#1890ff',
        bgColor: '#e6f7ff',
        showCurrency: true,
        navigateTo: '/reports/bill-wise-sale'
      },
      {
        title: 'Pending Bills',
        value: dashboardData.billSummary?.pending_amount || 0,
        icon: <ClockCircleOutlined />,
        color: '#faad14',
        bgColor: '#fffbe6',
        showCurrency: true,
        navigateTo: '/collection/summary'
      },
      {
        title: 'Paid Bills',
        value: dashboardData.billSummary?.paid_amount || 0,
        icon: <CheckCircleOutlined />,
        color: '#52c41a',
        bgColor: '#f6ffed',
        showCurrency: true,
        navigateTo: '/collection/summary'
      },
      {
        title: 'Entertainment',
        value: dashboardData.entertainment?.total_entertainment || 0,
        icon: <FileTextOutlined />,
        color: '#722ed1',
        bgColor: '#f9f0ff',
        showCurrency: true
      },
      {
        title: 'Total Discount',
        value: dashboardData.summary.discounts?.total_discount || 0,
        icon: <FallOutlined />,
        color: '#eb2f96',
        bgColor: '#fff0f6',
        showCurrency: true
      }
    ];

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {cards.map((card, index) => (
          <Col key={index} xs={24} sm={12} md={12} lg={12} xl={Math.floor(24 / cards.length)}>
            <Card 
              bordered={true}
              hoverable={!!card.navigateTo}
              onClick={() => card.navigateTo && navigate(card.navigateTo)}
              style={{ 
                cursor: card.navigateTo ? 'pointer' : 'default',
                height: '100%'
              }}
            >
              <Statistic
                title={card.title}
                value={card.value}
                precision={0}
                valueStyle={{ color: card.color }}
                prefix={card.showCurrency ? '฿' : ''}
                suffix={
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '24px',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: card.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: card.color
                  }}>
                    {card.icon}
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Weekly Sales Analysis Chart Data
  const getWeeklySalesData = () => {
    // console.log('Weekly Sales Data:', dashboardData.weeklySales);
    
    if (!dashboardData.weeklySales || dashboardData.weeklySales.length === 0) {
      // Generate last 8 weeks labels for empty state
      const emptyLabels = [];
      const today = new Date();
      for (let i = 7; i >= 0; i--) {
        const weekDate = new Date(today);
        weekDate.setDate(today.getDate() - (i * 7));
        const weekNum = Math.ceil((weekDate.getDate() + 6) / 7);
        emptyLabels.push(`Week ${weekNum}`);
      }
      
      return {
        labels: emptyLabels,
        datasets: [
          {
            label: 'Total Sales (No data available)',
            data: new Array(8).fill(0),
            backgroundColor: 'rgba(99, 102, 241, 0.3)',
            borderColor: '#6366f1',
            borderWidth: 2,
            borderRadius: 6,
            borderDash: [5, 5]
          }
        ]
      };
    }

    // Create labels with week info
    const labels = dashboardData.weeklySales.map(item => {
      if (item.week_start && item.week_end) {
        return `Week ${item.week_number} (${new Date(item.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(item.week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
      }
      return `Week ${item.week_number} - ${item.year}`;
    });
    
    // Extract sales data and convert to numbers
    const salesData = dashboardData.weeklySales.map(item => parseFloat(item.total_sales) || 0);

    // console.log('Chart Labels:', labels);
    // console.log('Chart Data:', salesData);

    return {
      labels,
      datasets: [
        {
          label: 'Total Sales',
          data: salesData,
          backgroundColor: '#6366f1',
          borderColor: '#4f46e5',
          borderWidth: 2,
          borderRadius: 6
        }
      ]
    };
  };

  // Category Sales Pie Chart
  const getCategorySalesData = () => {
    return {
      labels: ['Food', 'Bar', 'Shisha'],
      datasets: [
        {
          data: [
            dashboardData.summary.sales?.food_sale || 0,
            dashboardData.summary.sales?.bar_sale || 0,
            dashboardData.summary.sales?.shisha_sale || 0
          ],
          backgroundColor: ['#10b981', '#f59e0b', '#ec4899'],
          borderWidth: 0
        }
      ]
    };
  };

  // Payment Methods Bar Chart
  const getPaymentMethodsData = () => {
    const methods = dashboardData.paymentMethods || {};
    return {
      labels: ['Cash', 'Card', 'Bank Transfer'],
      datasets: [
        {
          label: 'Amount',
          data: [
            methods.cash || 0,
            methods.card || 0,
            methods.bank_transfer || 0
          ],
          backgroundColor: ['#6366f1', '#ec4899', '#10b981'],
          borderRadius: 6
        }
      ]
    };
  };

  // Chart Options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#6366f1',
        borderWidth: 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12
          }
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Helper function to sort best selling items
  const getSortedBestSellingItems = (items, sortBy) => {
    if (!items || items.length === 0) return [];
    
    const sortedItems = [...items];
    if (sortBy === 'quantity') {
      return sortedItems.sort((a, b) => 
        (parseFloat(b.total_quantity_sold) || 0) - (parseFloat(a.total_quantity_sold) || 0)
      );
    } else {
      return sortedItems.sort((a, b) => 
        (parseFloat(b.total_sales_amount) || 0) - (parseFloat(a.total_sales_amount) || 0)
      );
    }
  };

  // Revenue Trends Analytics Data
  const getRevenueTrendsData = () => {
    if (!dashboardData.salesChart || dashboardData.salesChart.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    let labels = [];
    let revenueData = [];
    let discountData = [];

    if (viewMode === 'daily') {
      labels = dashboardData.salesChart.map(item => 
        new Date(item.sale_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      );
      revenueData = dashboardData.salesChart.map(item => parseFloat(item.total_amount) || 0);
      discountData = dashboardData.salesChart.map(item => parseFloat(item.total_discount) || 0);
    } else if (viewMode === 'weekly') {
      labels = dashboardData.salesChart.map(item => `Week ${item.week_number}`);
      revenueData = dashboardData.salesChart.map(item => parseFloat(item.total_sales) || 0);
      discountData = dashboardData.salesChart.map(item => parseFloat(item.total_discount) || 0);
    } else {
      labels = dashboardData.salesChart.map(item => 
        `${item.month_name?.substring(0, 3)} ${item.year}`
      );
      revenueData = dashboardData.salesChart.map(item => parseFloat(item.total_sales) || 0);
      discountData = dashboardData.salesChart.map(item => parseFloat(item.total_discount) || 0);
    }

    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenueData,
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 6
        },
        {
          label: 'Discounts',
          data: discountData,
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#ef4444',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 5
        }
      ]
    };
  };

  // Enhanced Category Doughnut Chart
  const getCategoryDoughnutData = () => {
    const barAmount = dashboardData.categoryBreakdown?.bar_amount || 0;
    const foodAmount = dashboardData.categoryBreakdown?.food_amount || 0;
    const shishaAmount = dashboardData.categoryBreakdown?.shisha_amount || 0;
    const total = barAmount + foodAmount + shishaAmount;

    return {
      labels: ['Bar Sales', 'Food Sales', 'Shisha Sales'],
      datasets: [
        {
          data: [barAmount, foodAmount, shishaAmount],
          backgroundColor: [
            'rgba(99, 102, 241, 0.8)',
            'rgba(236, 72, 153, 0.8)',
            'rgba(16, 185, 129, 0.8)'
          ],
          borderColor: ['#6366f1', '#ec4899', '#10b981'],
          borderWidth: 3,
          hoverOffset: 10
        }
      ]
    };
  };

  // Calculate analytics metrics
  const getAnalyticsSummary = () => {
    const salesData = dashboardData.salesChart || [];
    if (salesData.length === 0) {
      return {
        avgDailySales: 0,
        totalRevenue: 0,
        growthRate: 0,
        peakSalesDay: 'N/A'
      };
    }

    let revenues = [];
    let labels = [];

    if (viewMode === 'daily') {
      revenues = salesData.map(item => parseFloat(item.total_amount) || 0);
      labels = salesData.map(item => new Date(item.sale_date).toLocaleDateString());
    } else if (viewMode === 'weekly') {
      revenues = salesData.map(item => parseFloat(item.total_sales) || 0);
      labels = salesData.map(item => `Week ${item.week_number}`);
    } else {
      revenues = salesData.map(item => parseFloat(item.total_sales) || 0);
      labels = salesData.map(item => item.month_name);
    }

    const totalRevenue = revenues.reduce((sum, val) => sum + val, 0);
    const avgSales = totalRevenue / revenues.length;
    
    // Calculate growth rate (last vs first period)
    let growthRate = 0;
    if (revenues.length > 1 && revenues[0] > 0) {
      growthRate = ((revenues[revenues.length - 1] - revenues[0]) / revenues[0]) * 100;
    }

    // Find peak sales period
    const maxRevenue = Math.max(...revenues);
    const peakIndex = revenues.indexOf(maxRevenue);
    const peakSalesDay = labels[peakIndex] || 'N/A';

    return {
      avgDailySales: avgSales,
      totalRevenue,
      growthRate,
      peakSalesDay,
      maxRevenue
    };
  };

  const analytics = getAnalyticsSummary();

  // Revenue Trends Chart Options
  const revenueTrendsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#6366f1',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ฿${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value) {
            return '฿' + (value / 1000).toFixed(0) + 'K';
          }
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Doughnut Chart Options
  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 20,
          font: {
            size: 13,
            weight: '500'
          },
          usePointStyle: true,
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return {
                  text: `${label}: ${percentage}%`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ฿${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '65%'
  };

  // Table Columns
  const purchaseColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150
    },
    {
      title: 'Items',
      dataIndex: 'unique_items',
      key: 'unique_items',
      width: 100,
      align: 'center'
    },
    {
      title: 'Total Value',
      dataIndex: 'total_purchase_value',
      key: 'total_purchase_value',
      width: 150,
      align: 'right',
      render: (val) => `฿${parseFloat(val || 0).toFixed(2)}`
    }
  ];

  const expenseColumns = [
    {
      title: 'Head',
      dataIndex: 'head',
      key: 'head',
      width: 150
    },
    {
      title: 'Sub Head',
      dataIndex: 'subhead',
      key: 'subhead',
      width: 150
    },
    {
      title: 'Credit',
      dataIndex: 'total_credit',
      key: 'total_credit',
      width: 120,
      align: 'right',
      render: (val) => `฿${parseFloat(val || 0).toFixed(2)}`
    },
    {
      title: 'Debit',
      dataIndex: 'total_debit',
      key: 'total_debit',
      width: 120,
      align: 'right',
      render: (val) => `฿${parseFloat(val || 0).toFixed(2)}`
    },
    {
      title: 'Outstanding',
      dataIndex: 'total_outstanding_balance',
      key: 'total_outstanding_balance',
      width: 150,
      align: 'right',
      render: (val) => `฿${parseFloat(val || 0).toFixed(2)}`
    }
  ];

  const customerColumns = [
    {
      title: 'Customer Name',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120
    },
    {
      title: 'Outstanding Balance',
      dataIndex: 'outstanding_balance',
      key: 'outstanding_balance',
      width: 150,
      align: 'right',
      render: (val) => `₹${parseFloat(val || 0).toFixed(2)}`
    }
  ];

  return (
    <Layout>
      <div className="dashboard-page">
        <div className="dashboard-content">
          <Spin spinning={loading}>
            {/* Date Information Section */}
            <Card 
              bordered={false}
              style={{ 
                marginBottom: '24px', 
                background: 'linear-gradient(135deg, #1890ff 0%, #52c41a 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <Row gutter={16} align="middle">
                <Col xs={24} sm={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      background: 'rgba(255,255,255,0.25)', 
                      borderRadius: '8px', 
                      padding: '10px 14px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <ShoppingCartOutlined style={{ fontSize: '24px', color: '#fff' }} />
                    </div>
                    <div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' }}>
                        SALE DATE (Setup Date)
                      </div>
                      <div style={{ color: '#fff', fontSize: '22px', fontWeight: 'bold', marginTop: '4px' }}>
                        {setupDate ? new Date(setupDate).toLocaleDateString('en-GB', { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric' 
                        }) : 'Loading...'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end' }}>
                    <div>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.85)', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textAlign: 'right',
                        letterSpacing: '0.5px'
                      }}>
                        CURRENT DATE
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '22px', 
                        fontWeight: 'bold', 
                        marginTop: '4px',
                        textAlign: 'right'
                      }}>
                        {new Date().toLocaleDateString('en-GB', { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </div>
                    </div>
                    <div style={{ 
                      background: 'rgba(255,255,255,0.25)', 
                      borderRadius: '8px', 
                      padding: '10px 14px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <ClockCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <TypographyTitle 
                level={2} 
                style={{ 
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1f2937'
                }}
              >
                Admin Dashboard
              </TypographyTitle>
              <Select
                value={viewMode}
                onChange={setViewMode}
                style={{ width: 150 }}
                size="large"
              >
                <Option value="daily">Daily View</Option>
                <Option value="weekly">Weekly View</Option>
                <Option value="monthly">Monthly View</Option>
              </Select>
            </div>

            {/* Summary Cards */}
            <SummaryCards />

            {/* Analytics Summary Section - Hidden as per user request */}
            {/* <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>              
              <Col xs={24} lg={6}>
                <Card className="analytics-card" style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  height: '100%'
                }}>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px' }}>Average {viewMode === 'daily' ? 'Daily' : viewMode === 'weekly' ? 'Weekly' : 'Monthly'} Sales</span>}
                    value={analytics.avgDailySales}
                    precision={0}
                    valueStyle={{ color: '#fff', fontSize: '24px', fontWeight: 'bold' }}
                    prefix="฿"
                    suffix={<RiseOutlined style={{ fontSize: '18px' }} />}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={6}>
                <Card className="analytics-card" style={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: '12px',
                  height: '100%'
                }}>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px' }}>Total Revenue</span>}
                    value={analytics.totalRevenue}
                    precision={0}
                    valueStyle={{ color: '#fff', fontSize: '24px', fontWeight: 'bold' }}
                    prefix="฿"
                    suffix={<DollarOutlined style={{ fontSize: '18px' }} />}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={6}>
                <Card className="analytics-card" style={{ 
                  background: analytics.growthRate >= 0 
                    ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                    : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  borderRadius: '12px',
                  height: '100%'
                }}>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px' }}>Growth Rate</span>}
                    value={Math.abs(analytics.growthRate)}
                    precision={1}
                    valueStyle={{ color: '#fff', fontSize: '24px', fontWeight: 'bold' }}
                    suffix={<span style={{ fontSize: '18px' }}>%</span>}
                    prefix={analytics.growthRate >= 0 ? <RiseOutlined style={{ fontSize: '18px' }} /> : <FallOutlined style={{ fontSize: '18px' }} />}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={6}>
                <Card className="analytics-card" style={{ 
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  borderRadius: '12px',
                  height: '100%'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', marginBottom: '8px' }}>Peak Sales Period</div>
                    <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>{analytics.peakSalesDay}</div>
                    <div style={{ color: '#fff', fontSize: '14px' }}>฿{analytics.maxRevenue?.toLocaleString() || 0}</div>
                  </div>
                </Card>
              </Col>
            </Row> */}

            {/* Revenue Trends Analytics */}
            <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
              <Col xs={24} lg={16}>
                <Card 
                  className="chart-card" 
                  variant="borderless"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <RiseOutlined style={{ fontSize: '20px', marginRight: '10px', color: 'white' }} />
                      <span style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>
                        Revenue Trends Analytics ({viewMode.charAt(0).toUpperCase() + viewMode.slice(1)})
                      </span>
                    </div>
                  }
                  style={{ height: '400px' }}
                >
                  <div style={{ height: '320px' }}>
                    <Line data={getRevenueTrendsData()} options={revenueTrendsOptions} />
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card 
                  className="chart-card" 
                  variant="borderless"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <ShoppingCartOutlined style={{ fontSize: '20px', marginRight: '10px', color: 'white' }} />
                      <span style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Sales Distribution</span>
                    </div>
                  }
                  style={{ height: '400px' }}
                >
                  <div style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Doughnut data={getCategoryDoughnutData()} options={doughnutChartOptions} />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Best Selling Items Section */}
            <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
              <Col xs={24} lg={12}>
                <Card 
                  className="data-table-card" 
                  variant="borderless" 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>🍺 Top 5 Best Selling Bar Items</span>
                      <AntSpace size="small">
                        <Button
                          type={bestSellingSort === 'value' ? 'primary' : 'default'}
                          icon={<DollarOutlined />}
                          onClick={() => setBestSellingSort('value')}
                          title="Sort by Value"
                          size="small"
                        />
                        <Button
                          type={bestSellingSort === 'quantity' ? 'primary' : 'default'}
                          icon={<ShoppingOutlined />}
                          onClick={() => setBestSellingSort('quantity')}
                          title="Sort by Quantity"
                          size="small"
                        />
                      </AntSpace>
                    </div>
                  }
                >
                  <Table
                    columns={[
                      {
                        title: 'Rank',
                        key: 'rank',
                        width: 60,
                        align: 'center',
                        render: (text, record, index) => (
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: index === 0 ? '#f59e0b' : index === 1 ? '#9ca3af' : index === 2 ? '#cd7f32' : '#6b7280',
                            fontSize: '16px'
                          }}>
                            {index + 1}
                          </span>
                        )
                      },
                      {
                        title: 'Item Name',
                        dataIndex: 'item_name',
                        key: 'item_name',
                        ellipsis: true
                      },
                      {
                        title: 'Qty Sold',
                        dataIndex: 'total_quantity_sold',
                        key: 'total_quantity_sold',
                        align: 'center',
                        width: 100,
                        render: (val) => <strong>{parseFloat(val || 0).toFixed(0)}</strong>
                      },
                      {
                        title: 'Sales Amount',
                        dataIndex: 'total_sales_amount',
                        key: 'total_sales_amount',
                        align: 'right',
                        width: 130,
                        render: (val) => (
                          <strong style={{ color: '#10b981' }}>
                            ฿{parseFloat(val || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </strong>
                        )
                      }
                    ]}
                    dataSource={getSortedBestSellingItems(dashboardData.bestSellingItems?.bar?.items || [], bestSellingSort)}
                    rowKey="item_name"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: 'No data available' }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card 
                  className="data-table-card" 
                  variant="borderless" 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>🍽️ Top 5 Best Selling Food Items</span>
                      <AntSpace size="small">
                        <Button
                          type={bestSellingSort === 'value' ? 'primary' : 'default'}
                          icon={<DollarOutlined />}
                          onClick={() => setBestSellingSort('value')}
                          title="Sort by Value"
                          size="small"
                        />
                        <Button
                          type={bestSellingSort === 'quantity' ? 'primary' : 'default'}
                          icon={<ShoppingOutlined />}
                          onClick={() => setBestSellingSort('quantity')}
                          title="Sort by Quantity"
                          size="small"
                        />
                      </AntSpace>
                    </div>
                  }
                >
                  <Table
                    columns={[
                      {
                        title: 'Rank',
                        key: 'rank',
                        width: 60,
                        align: 'center',
                        render: (text, record, index) => (
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: index === 0 ? '#f59e0b' : index === 1 ? '#9ca3af' : index === 2 ? '#cd7f32' : '#6b7280',
                            fontSize: '16px'
                          }}>
                            {index + 1}
                          </span>
                        )
                      },
                      {
                        title: 'Item Name',
                        dataIndex: 'item_name',
                        key: 'item_name',
                        ellipsis: true
                      },
                      {
                        title: 'Qty Sold',
                        dataIndex: 'total_quantity_sold',
                        key: 'total_quantity_sold',
                        align: 'center',
                        width: 100,
                        render: (val) => <strong>{parseFloat(val || 0).toFixed(0)}</strong>
                      },
                      {
                        title: 'Sales Amount',
                        dataIndex: 'total_sales_amount',
                        key: 'total_sales_amount',
                        align: 'right',
                        width: 130,
                        render: (val) => (
                          <strong style={{ color: '#10b981' }}>
                            ฿{parseFloat(val || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </strong>
                        )
                      }
                    ]}
                    dataSource={getSortedBestSellingItems(dashboardData.bestSellingItems?.food?.items || [], bestSellingSort)}
                    rowKey="item_name"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: 'No data available' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Weekly Sale Analysis Chart - Full Width */}
            <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
              <Col xs={24}>
                <Card 
                  className="chart-card" 
                  variant="borderless" 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Weekly Sale Analysis</span>
                      {(!dashboardData.weeklySales || dashboardData.weeklySales.length === 0) && (
                        <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#f59e0b' }}>
                          ⚠️ No closed day data available in last 8 weeks
                        </span>
                      )}
                    </div>
                  }
                >
                  <div style={{ height: '300px' }}>
                    <Bar data={getWeeklySalesData()} options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 12,
                          titleColor: '#fff',
                          bodyColor: '#fff',
                          borderColor: '#6366f1',
                          borderWidth: 1,
                          callbacks: {
                            label: function(context) {
                              return `Sales: ฿${context.parsed.y.toLocaleString()}`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(0, 0, 0, 0.05)'
                          },
                          ticks: {
                            callback: function(value) {
                              return '฿' + value.toLocaleString();
                            }
                          }
                        },
                        x: {
                          grid: {
                            display: false
                          }
                        }
                      }
                    }} />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Category Breakdown and Purchase Summary Row */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col xs={24} lg={12}>
                <Card className="chart-card" variant="borderless" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div className="table-header-section" style={{ marginBottom: '16px' }}>
                    <ShoppingCartOutlined style={{ fontSize: '20px', marginRight: '10px', color: 'white' }} />
                    <span style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Category Breakdown Summary</span>
                  </div>
                  <div style={{ flex: 1, overflow: 'auto' }}>
                  <Table
                      columns={[
                        {
                          title: 'CATEGORY',
                          dataIndex: 'category',
                          key: 'category',
                          render: (text, record) => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '1.2rem' }}>{record.icon}</span>
                              <strong style={{ color: record.color }}>{text}</strong>
                            </div>
                          )
                        },
                        {
                          title: 'DESCRIPTION',
                          dataIndex: 'description',
                          key: 'description',
                          render: (text) => <span style={{ color: '#6b7280' }}>{text}</span>
                        },
                        {
                          title: 'AMOUNT',
                          dataIndex: 'amount',
                          key: 'amount',
                          align: 'right',
                          render: (val, record) => (
                            <strong style={{ color: record.color, fontSize: record.isTotal ? '1.1rem' : '1rem' }}>
                              ฿{val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </strong>
                          )
                        }
                      ]}
                      dataSource={[
                        {
                          key: '1',
                          category: 'Bar Sales',
                          description: 'Beverages & Drinks',
                          amount: dashboardData.categoryBreakdown?.bar_amount || 0,
                          icon: '🍺',
                          color: '#2e7d32'
                        },
                        {
                          key: '2',
                          category: 'Food Sales',
                          description: 'Meals & Cuisine',
                          amount: dashboardData.categoryBreakdown?.food_amount || 0,
                          icon: '🍽️',
                          color: '#c2185b'
                        },
                        {
                          key: '3',
                          category: 'Shisha Sales',
                          description: 'Hookah & Flavors',
                          amount: dashboardData.categoryBreakdown?.shisha_amount || 0,
                          icon: '💨',
                          color: '#1976d2'
                        },
                        {
                          key: '4',
                          category: 'Total Sales',
                          description: 'Combined Revenue',
                          amount: (dashboardData.categoryBreakdown?.bar_amount || 0) + 
                                 (dashboardData.categoryBreakdown?.food_amount || 0) + 
                                 (dashboardData.categoryBreakdown?.shisha_amount || 0),
                          icon: '📊',
                          color: '#667eea',
                          isTotal: true
                        }
                      ]}
                      pagination={false}
                      size="small"
                      rowClassName={(record) => record.isTotal ? 'summary-row' : ''}
                    />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card className="data-table-card" variant="borderless" title="Purchase Summary">
                    <Table
                      columns={purchaseColumns}
                      dataSource={dashboardData.purchases}
                      rowKey="category"
                      pagination={{ pageSize: 5, size: 'small' }}
                      size="small"
                      scroll={{ x: 500 }}
                    />
                  </Card>
                </Col>
              </Row>

            {/* Payment Methods Chart */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col xs={24}>
                <Card className="chart-card" variant="borderless" title="Payment Methods Distribution">
                  <div style={{ height: '250px' }}>
                    <Bar data={getPaymentMethodsData()} options={barChartOptions} />
                  </div>
                </Card>
              </Col>
            </Row>
          </Spin>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
